import React from 'react';
import { Typography, Card, Space, Divider, Alert, List } from 'antd';
import { 
  SafetyOutlined, 
  LockOutlined, 
  UserOutlined, 
  GlobalOutlined,
  MailOutlined,
  PhoneOutlined,
  DatabaseOutlined,
  EyeOutlined
} from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;

const Privacy = () => {
  return (
    <div className="privacy-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-green-600 to-green-400 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <SafetyOutlined className="text-6xl mb-6" />
          <Title level={1} className="text-white mb-6">
            Politique de Confidentialité
          </Title>
          <Paragraph className="text-lg opacity-90 max-w-2xl mx-auto">
            Chez Mercuri Shop, nous nous engageons à protéger votre vie privée et vos données personnelles. 
            Cette politique explique comment nous collectons, utilisons et protégeons vos informations.
          </Paragraph>
          <Text className="text-sm opacity-75">
            Dernière mise à jour : 28 janvier 2025
          </Text>
        </div>
      </section>

      {/* Contenu principal */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          
          {/* Introduction */}
          <Card className="mb-8 shadow-md">
            <Title level={2} className="flex items-center gap-3 mb-4">
              <UserOutlined className="text-blue-500" />
              1. Introduction
            </Title>
            <Paragraph className="text-base">
              Mercuri Shop ("nous", "notre", "nos") respecte votre vie privée et s'engage à protéger vos données personnelles. 
              Cette politique de confidentialité vous informe sur la façon dont nous traitons vos données personnelles 
              lorsque vous utilisez notre plateforme d'automatisation WhatsApp.
            </Paragraph>
            <Paragraph className="text-base">
              Cette politique s'applique à tous les utilisateurs de nos services, qu'ils soient des entreprises, 
              des particuliers ou des clients finaux interagissant avec nos systèmes automatisés.
            </Paragraph>
          </Card>

          {/* Données collectées */}
          <Card className="mb-8 shadow-md">
            <Title level={2} className="flex items-center gap-3 mb-4">
              <DatabaseOutlined className="text-blue-500" />
              2. Données que nous collectons
            </Title>
            
            <Title level={4} className="mt-6 mb-3">2.1 Informations d'inscription et de profil</Title>
            <List
              size="small"
              dataSource={[
                'Nom et prénom',
                'Adresse e-mail',
                'Numéro de téléphone',
                'Nom de l\'entreprise',
                'Secteur d\'activité',
                'Pays et ville'
              ]}
              renderItem={(item) => (
                <List.Item>
                  <Text>• {item}</Text>
                </List.Item>
              )}
            />

            <Title level={4} className="mt-6 mb-3">2.2 Données WhatsApp</Title>
            <List
              size="small"
              dataSource={[
                'Messages échangés via votre compte WhatsApp Business',
                'Numéros de téléphone de vos contacts',
                'Métadonnées des conversations (horodatage, statut de livraison)',
                'Médias partagés (images, documents, audio)',
                'Informations de profil WhatsApp de vos contacts'
              ]}
              renderItem={(item) => (
                <List.Item>
                  <Text>• {item}</Text>
                </List.Item>
              )}
            />

            <Title level={4} className="mt-6 mb-3">2.3 Données d'utilisation</Title>
            <List
              size="small"
              dataSource={[
                'Logs de connexion et d\'activité',
                'Adresse IP et informations sur l\'appareil',
                'Données de navigation sur notre plateforme',
                'Statistiques d\'utilisation des fonctionnalités',
                'Données de performance et d\'erreurs'
              ]}
              renderItem={(item) => (
                <List.Item>
                  <Text>• {item}</Text>
                </List.Item>
              )}
            />

            <Title level={4} className="mt-6 mb-3">2.4 Données de paiement</Title>
            <Paragraph className="text-base">
              Nous ne stockons pas vos informations de carte de crédit. Les paiements sont traités de manière 
              sécurisée par nos partenaires de paiement certifiés PCI DSS.
            </Paragraph>
          </Card>

          {/* Utilisation des données */}
          <Card className="mb-8 shadow-md">
            <Title level={2} className="flex items-center gap-3 mb-4">
              <EyeOutlined className="text-blue-500" />
              3. Comment nous utilisons vos données
            </Title>
            
            <Title level={4} className="mt-6 mb-3">3.1 Fourniture du service</Title>
            <List
              size="small"
              dataSource={[
                'Automatisation de vos conversations WhatsApp',
                'Génération de réponses intelligentes via IA',
                'Gestion de vos contacts et conversations',
                'Synchronisation avec vos systèmes tiers (Shopify, etc.)',
                'Génération de rapports et statistiques'
              ]}
              renderItem={(item) => (
                <List.Item>
                  <Text>• {item}</Text>
                </List.Item>
              )}
            />

            <Title level={4} className="mt-6 mb-3">3.2 Amélioration du service</Title>
            <List
              size="small"
              dataSource={[
                'Analyse des performances de nos algorithmes d\'IA',
                'Développement de nouvelles fonctionnalités',
                'Résolution de problèmes techniques',
                'Optimisation de l\'expérience utilisateur'
              ]}
              renderItem={(item) => (
                <List.Item>
                  <Text>• {item}</Text>
                </List.Item>
              )}
            />

            <Title level={4} className="mt-6 mb-3">3.3 Communication</Title>
            <List
              size="small"
              dataSource={[
                'Notifications importantes sur le service',
                'Mises à jour de sécurité',
                'Support technique',
                'Informations sur les nouvelles fonctionnalités (avec votre consentement)'
              ]}
              renderItem={(item) => (
                <List.Item>
                  <Text>• {item}</Text>
                </List.Item>
              )}
            />
          </Card>

          {/* Partage des données */}
          <Card className="mb-8 shadow-md">
            <Title level={2} className="flex items-center gap-3 mb-4">
              <GlobalOutlined className="text-blue-500" />
              4. Partage de vos données
            </Title>
            
            <Alert
              message="Principe fondamental"
              description="Nous ne vendons jamais vos données personnelles à des tiers."
              type="info"
              showIcon
              className="mb-6"
            />

            <Title level={4} className="mt-6 mb-3">4.1 Partenaires de service</Title>
            <Paragraph className="text-base">
              Nous pouvons partager vos données avec des prestataires de services de confiance qui nous aident à :
            </Paragraph>
            <List
              size="small"
              dataSource={[
                'Hébergement et infrastructure cloud (AWS, Google Cloud)',
                'Services d\'IA et de traitement du langage naturel',
                'Traitement des paiements',
                'Support client et assistance technique',
                'Analyse et monitoring des performances'
              ]}
              renderItem={(item) => (
                <List.Item>
                  <Text>• {item}</Text>
                </List.Item>
              )}
            />

            <Title level={4} className="mt-6 mb-3">4.2 Obligations légales</Title>
            <Paragraph className="text-base">
              Nous pouvons divulguer vos données si la loi l'exige ou pour :
            </Paragraph>
            <List
              size="small"
              dataSource={[
                'Répondre à une assignation ou ordonnance judiciaire',
                'Protéger nos droits légaux',
                'Prévenir la fraude ou les activités illégales',
                'Assurer la sécurité de nos utilisateurs'
              ]}
              renderItem={(item) => (
                <List.Item>
                  <Text>• {item}</Text>
                </List.Item>
              )}
            />
          </Card>

          {/* Sécurité */}
          <Card className="mb-8 shadow-md">
            <Title level={2} className="flex items-center gap-3 mb-4">
              <LockOutlined className="text-blue-500" />
              5. Sécurité de vos données
            </Title>
            
            <Paragraph className="text-base">
              Nous mettons en place des mesures de sécurité techniques et organisationnelles appropriées pour 
              protéger vos données contre :
            </Paragraph>
            
            <List
              size="small"
              dataSource={[
                'Accès non autorisé',
                'Divulgation accidentelle',
                'Altération ou destruction',
                'Perte de données'
              ]}
              renderItem={(item) => (
                <List.Item>
                  <Text>• {item}</Text>
                </List.Item>
              )}
            />

            <Title level={4} className="mt-6 mb-3">5.1 Mesures techniques</Title>
            <List
              size="small"
              dataSource={[
                'Chiffrement des données en transit et au repos',
                'Authentification multi-facteurs',
                'Contrôles d\'accès stricts',
                'Surveillance continue des systèmes',
                'Sauvegardes régulières et sécurisées'
              ]}
              renderItem={(item) => (
                <List.Item>
                  <Text>• {item}</Text>
                </List.Item>
              )}
            />

            <Title level={4} className="mt-6 mb-3">5.2 Mesures organisationnelles</Title>
            <List
              size="small"
              dataSource={[
                'Formation régulière de notre équipe sur la sécurité',
                'Politiques strictes d\'accès aux données',
                'Audits de sécurité réguliers',
                'Plan de réponse aux incidents',
                'Accords de confidentialité avec tous les employés'
              ]}
              renderItem={(item) => (
                <List.Item>
                  <Text>• {item}</Text>
                </List.Item>
              )}
            />
          </Card>

          {/* Vos droits */}
          <Card className="mb-8 shadow-md">
            <Title level={2} className="flex items-center gap-3 mb-4">
              <SafetyOutlined className="text-blue-500" />
              6. Vos droits
            </Title>
            
            <Paragraph className="text-base">
              Vous disposez des droits suivants concernant vos données personnelles :
            </Paragraph>
            
            <Title level={4} className="mt-6 mb-3">6.1 Droit d'accès</Title>
            <Paragraph className="text-base">
              Vous pouvez demander une copie de toutes les données personnelles que nous détenons sur vous.
            </Paragraph>

            <Title level={4} className="mt-6 mb-3">6.2 Droit de rectification</Title>
            <Paragraph className="text-base">
              Vous pouvez demander la correction de données inexactes ou incomplètes.
            </Paragraph>

            <Title level={4} className="mt-6 mb-3">6.3 Droit à l'effacement</Title>
            <Paragraph className="text-base">
              Vous pouvez demander la suppression de vos données dans certaines circonstances.
            </Paragraph>

            <Title level={4} className="mt-6 mb-3">6.4 Droit à la portabilité</Title>
            <Paragraph className="text-base">
              Vous pouvez demander le transfert de vos données vers un autre service.
            </Paragraph>

            <Title level={4} className="mt-6 mb-3">6.5 Droit d'opposition</Title>
            <Paragraph className="text-base">
              Vous pouvez vous opposer au traitement de vos données à des fins de marketing.
            </Paragraph>

            <Alert
              message="Exercer vos droits"
              description="Pour exercer ces droits, contactez-nous à shopmercuri@gmail.com avec une pièce d'identité."
              type="success"
              showIcon
              className="mt-6"
            />
          </Card>

          {/* Conservation des données */}
          <Card className="mb-8 shadow-md">
            <Title level={2} className="flex items-center gap-3 mb-4">
              <DatabaseOutlined className="text-blue-500" />
              7. Conservation des données
            </Title>
            
            <List
              size="small"
              dataSource={[
                'Données de compte : conservées tant que votre compte est actif',
                'Messages WhatsApp : conservés selon vos paramètres (maximum 2 ans)',
                'Logs techniques : conservés 12 mois maximum',
                'Données de facturation : conservées 7 ans (obligation légale)',
                'Données marketing : supprimées immédiatement sur demande'
              ]}
              renderItem={(item) => (
                <List.Item>
                  <Text>• {item}</Text>
                </List.Item>
              )}
            />

            <Paragraph className="text-base mt-4">
              Après suppression de votre compte, nous conservons uniquement les données nécessaires pour 
              respecter nos obligations légales et réglementaires.
            </Paragraph>
          </Card>

          {/* Cookies */}
          <Card className="mb-8 shadow-md">
            <Title level={2} className="flex items-center gap-3 mb-4">
              <GlobalOutlined className="text-blue-500" />
              8. Cookies et technologies similaires
            </Title>
            
            <Paragraph className="text-base">
              Nous utilisons des cookies et technologies similaires pour :
            </Paragraph>
            
            <List
              size="small"
              dataSource={[
                'Maintenir votre session de connexion',
                'Mémoriser vos préférences',
                'Analyser l\'utilisation de notre plateforme',
                'Améliorer les performances',
                'Personnaliser votre expérience'
              ]}
              renderItem={(item) => (
                <List.Item>
                  <Text>• {item}</Text>
                </List.Item>
              )}
            />

            <Paragraph className="text-base mt-4">
              Vous pouvez contrôler l'utilisation des cookies via les paramètres de votre navigateur.
            </Paragraph>
          </Card>

          {/* Transferts internationaux */}
          <Card className="mb-8 shadow-md">
            <Title level={2} className="flex items-center gap-3 mb-4">
              <GlobalOutlined className="text-blue-500" />
              9. Transferts internationaux
            </Title>
            
            <Paragraph className="text-base">
              Vos données peuvent être transférées et traitées dans des pays autres que votre pays de résidence. 
              Nous nous assurons que ces transferts respectent les réglementations applicables et que vos données 
              bénéficient d'un niveau de protection adéquat.
            </Paragraph>
            
            <Paragraph className="text-base">
              Nos principaux partenaires cloud sont situés dans l'Union Européenne et aux États-Unis, 
              avec des garanties contractuelles appropriées.
            </Paragraph>
          </Card>

          {/* Modifications */}
          <Card className="mb-8 shadow-md">
            <Title level={2} className="flex items-center gap-3 mb-4">
              <EyeOutlined className="text-blue-500" />
              10. Modifications de cette politique
            </Title>
            
            <Paragraph className="text-base">
              Nous pouvons mettre à jour cette politique de confidentialité périodiquement. 
              Nous vous informerons de tout changement significatif par :
            </Paragraph>
            
            <List
              size="small"
              dataSource={[
                'Notification par e-mail',
                'Bannière sur notre plateforme',
                'Mise à jour de la date en haut de cette page'
              ]}
              renderItem={(item) => (
                <List.Item>
                  <Text>• {item}</Text>
                </List.Item>
              )}
            />
          </Card>

          {/* Contact */}
          <Card className="shadow-md">
            <Title level={2} className="flex items-center gap-3 mb-4">
              <MailOutlined className="text-blue-500" />
              11. Nous contacter
            </Title>
            
            <Paragraph className="text-base">
              Pour toute question concernant cette politique de confidentialité ou le traitement de vos données :
            </Paragraph>
            
            <Space direction="vertical" size="middle" className="w-full">
              <div className="flex items-center gap-3">
                <MailOutlined className="text-blue-500" />
                <Text strong>E-mail :</Text>
                <Text>shopmercuri@gmail.com</Text>
              </div>
              
              <div className="flex items-center gap-3">
                <PhoneOutlined className="text-blue-500" />
                <Text strong>Téléphone :</Text>
                <Text>+225 07 59 95 47 08</Text>
              </div>
              
              <div className="flex items-center gap-3">
                <GlobalOutlined className="text-blue-500" />
                <Text strong>Adresse :</Text>
                <Text>Mercuri Shop, Abidjan, Côte d'Ivoire</Text>
              </div>
            </Space>

            <Alert
              message="Délégué à la Protection des Données"
              description="Pour les questions spécifiques au RGPD, contactez notre DPO : shopmercuri@gmail.com"
              type="info"
              showIcon
              className="mt-6"
            />
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Privacy; 